/**
 * Business Brand Plane Bridge — published-only theme → ConfigProvider merge (§12-C · §12-D)
 * @see docs/inflomatrix-constitution/ui-architecture/BRAND_PLANE_BRIDGE.md
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import type { BrandIdentity } from '@shared/site/design/brand-identity';
import { reduceBrandIdentityFromPublishedModel } from '@shared/site/design/brand-identity';
import { validateBrandForBusinessPlane } from '@shared/site/design/validate-brand-for-business-plane';
import { logger } from '@shared/logger';
import { beginCancellableRun } from '@shared/async/cancellableAsync';
import { fetchTenantSitePublishedModel } from '../../../site/frontend/services/sitePublishedModelApi';
import { createAntdThemeFromCssVars } from '../config/antdTheme';
import { BusinessBrandContext } from './BusinessBrandContext';

export interface BusinessBrandThemeProviderProps {
  tenantId: string;
  children: React.ReactNode;
}

export const BusinessBrandThemeProvider: React.FC<BusinessBrandThemeProviderProps> = ({
  tenantId,
  children,
}) => {
  const [brand, setBrand] = useState<BrandIdentity | undefined>(undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const run = beginCancellableRun();

    const loadPublishedBrand = async () => {
      setReady(false);
      try {
        const payload = await fetchTenantSitePublishedModel(tenantId);
        if (run.isCancelled()) {
          return;
        }

        if (!payload?.model?.theme) {
          logger.warn('BusinessBrandTheme: no published theme — constitutional fallback', {
            tenantId,
          });
          setBrand(undefined);
          return;
        }

        const identity = reduceBrandIdentityFromPublishedModel(payload.model);
        const validation = validateBrandForBusinessPlane(identity);

        if (!validation.valid) {
          logger.warn('BusinessBrandTheme: validation failed — constitutional fallback', {
            tenantId,
            issues: validation.issues,
          });
          setBrand(undefined);
          return;
        }

        setBrand(validation.identity);
      } catch (error) {
        if (run.isCancelled()) {
          return;
        }
        logger.error(
          'BusinessBrandTheme: published theme fetch failed — constitutional fallback',
          error instanceof Error ? error : undefined,
          { tenantId }
        );
        setBrand(undefined);
      } finally {
        if (!run.isCancelled()) {
          setReady(true);
        }
      }
    };

    void loadPublishedBrand();

    return () => {
      run.cancel();
    };
  }, [tenantId]);

  const theme = useMemo(
    () => createAntdThemeFromCssVars(brand !== undefined ? { brand } : undefined),
    [brand]
  );
  const brandContextValue = useMemo(() => ({ brand, ready }), [brand, ready]);

  return (
    <BusinessBrandContext.Provider value={brandContextValue}>
      <div
        data-testid="business-brand-accent-probe"
        data-accent={brand?.accent ?? ''}
        data-injection-level={brand?.injectionLevel ?? ''}
        data-ready={ready ? 'true' : 'false'}
        hidden
        aria-hidden="true"
      />
      {/* Nested ConfigProvider — merges over app.tsx outer (locale + constitutional theme). */}
      <ConfigProvider theme={theme}>{children}</ConfigProvider>
    </BusinessBrandContext.Provider>
  );
};

export default BusinessBrandThemeProvider;
